import { Box, Button, Grid, Typography, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import Dialog from 'components/dialog';
import FlexBox from 'components/flex-box';
import GoogleMaps from 'components/google-maps/google-maps';
import LoadingSpinner from 'components/loading-spinner';
import TextField from 'components/text-field';
import { AddressAccess } from 'contexts/account/types/account-company.type';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import Mui from 'materials';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { GoogleMapsMarkerInfo } from 'types/google-maps/google-maps-marker-info.type';
import MarkerContainer from 'views/delivery-board/components/marker-container';
import { useSiteSettingsState } from 'views/site-settings/context/site-settings-context';
import { PutLongitudeLatitudeRequest } from 'views/site-settings/features/general-settings/types/put-longitude-latitude-request.type';
import { useUserFeatures } from 'views/site-settings/hooks/use-user-features';
import { putLongitudeLatitude } from '../api/latitude-longitude';

type ParamTypes = {
  globalSiteId: string;
};

type Props = {
  site: AddressAccess | undefined;
};

const PAGE_NAME_ID = 'siteSettings.editSiteSettings.longitudeLatitude';

const LongitudeLatitude: React.FC<Props> = ({ site }) => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { canEditSiteSettingsLocation } = useUserFeatures();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const {
    siteSettingsQuery: { data: settings, isLoading },
  } = useSiteSettingsState();

  if (isLoading) return <LoadingSpinner />; // bad solution?
  if (!settings) return <></>;

  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  //#region FORM
  const { handleSubmit, control, reset } = useForm<PutLongitudeLatitudeRequest>({
    defaultValues: useMemo(() => {
      return {
        latitude: settings.latitude,
        longitude: settings.longitude,
      };
    }, [settings]),
    shouldUnregister: false,
  });

  const formValues = useWatch({
    control: control,
  });
  //#endregion

  //#region STATE
  const [markers, setMarkers] = useState<GoogleMapsMarkerInfo[]>([
    {
      name: site?.name || 'Your Site',
      url: `${site?.siteErpId}/${site?.id}`,
      lat: Number(settings.latitude),
      lng: Number(settings.longitude),
    },
  ]);
  //#endregion

  //#region POST DATA
  const updateLongitudeLatitude = usePost<AxiosResponse, PutLongitudeLatitudeRequest>(
    (request) => putLongitudeLatitude(params.globalSiteId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getSiteSettings', params.globalSiteId],
        exact: true
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      dialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion

  //#region HANDLERS
  const handleUpdateLongitudeLatitude = (request: PutLongitudeLatitudeRequest) => {
    updateLongitudeLatitude.mutate(request);
  };
  //#endregion

  useEffect(() => {
    reset({
      latitude: settings.latitude,
      longitude: settings.longitude,
    });

    setMarkers([
      {
        name: site?.name,
        lat: Number(settings.latitude) || 0,
        lng: Number(settings.longitude) || 0,
      },
    ]);
  }, [reset, settings]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h3" color={theme.palette.common.white}>
          <FormattedMessage id={`${PAGE_NAME_ID}.title`} />
        </Typography>
        {canEditSiteSettingsLocation && (
          <Dialog
            ref={dialogRef}
            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.title` })}
            maxWidth="md"
            button={
              <Button
                size="small"
                onClick={() => {
                  reset({
                    latitude: settings.latitude,
                    longitude: settings.longitude,
                  });
                }}>
                <FormattedMessage id={`${PAGE_NAME_ID}.dialog.button`} />
              </Button>
            }>
            <form onSubmit={handleSubmit(handleUpdateLongitudeLatitude)}>
              {updateLongitudeLatitude.isPending && <LoadingSpinner overlay />}
              <Box height={{ xs: '300px', md: '300px' }} pb={4}>
                <GoogleMaps
                  editLocation={true}
                  onClick={(event) => {
                    const { latLng } = event.detail;
                    if (latLng)
                      reset({
                        latitude: Number(latLng.lat.toFixed(6)),
                        longitude: Number(latLng.lng.toFixed(6)),
                      });
                  }}
                  center={{
                    lat: Number(formValues.latitude) || 0,
                    lng: Number(formValues.longitude) || 0,
                  }}>
                  <MarkerContainer
                    sites={[{ name: site?.name, lat: Number(formValues.latitude) || 0, lng: Number(formValues.longitude) || 0 }]}
                  />
                </GoogleMaps>
              </Box>
              <FlexBox>
                <TextField
                  type="number"
                  decimalScale={14}
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.latitudeLabel` })}
                  controller={{
                    name: 'latitude',
                    control: control,
                    rules: {
                      required: intl.formatMessage({ id: 'form.rules.required' }),
                      min: -90,
                      max: 90,
                    },
                  }}
                />
                <TextField
                  type="number"
                  decimalScale={14}
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.longitudeLabel` })}
                  controller={{
                    name: 'longitude',
                    control: control,
                    rules: {
                      required: intl.formatMessage({ id: 'form.rules.required' }),
                      min: -180,
                      max: 180,
                    },
                  }}
                />
                <Mui.FormControl fullWidth>
                  <Box display={'flex'} justifyContent={'end'}>
                    <Button type="submit">
                      <FormattedMessage id="general.save" />
                    </Button>
                  </Box>
                </Mui.FormControl>
              </FlexBox>
            </form>
          </Dialog>
        )}
      </Box>
      <Box position={'relative'} height={'400px'}>
        <GoogleMaps
          center={{
            lat: Number(settings.latitude) || 0,
            lng: Number(settings.longitude) || 0,
          }}>
          <Box
            color={theme.palette.darkBlue.main}
            position={'absolute'}
            bottom={30}
            width={{ xs: '70%', md: 'auto' }}
            left={10}
            p={2}
            borderRadius={2}
            right={{ xs: 4, md: 'auto' }}
            sx={{ backgroundColor: '#fff' }}>
            <Grid container spacing={4}>
              <Grid>
                <Typography variant="caption">
                  <FormattedMessage id={`${PAGE_NAME_ID}.latitudeLabel`} />
                </Typography>
                <Typography>{settings?.latitude}</Typography>
              </Grid>
              <Grid>
                <Typography variant="caption">
                  <FormattedMessage id={`${PAGE_NAME_ID}.longitudeLabel`} />
                </Typography>
                <Typography>{settings?.longitude}</Typography>
              </Grid>
            </Grid>
          </Box>
          <MarkerContainer sites={markers} />
        </GoogleMaps>
      </Box>
    </>
  );
};

export default React.memo(LongitudeLatitude);
